
import React from 'react';
import { CardInput } from '../../utils/kit/modules/cardDetails';
var valid = require("card-validator");


interface CardInputFieldState
{
    isTouched: boolean,
    isValid: boolean,
    isPotentiallyValid: boolean
}

export interface CardInputState
{
    cardValid: CardInputFieldState,
    zipValid: CardInputFieldState,
    nameValid: CardInputFieldState,
    cvcValid: CardInputFieldState,
    expirationValid: CardInputFieldState,
    errorMsg: string []
}

interface GACardInputProps
{
    onCardValid: (isValid: boolean) => void,
    onCardChange: (card: CardInput) => void,
    onErrorMessageChange?: (state: CardInputState) => void,
    disabled?: boolean
}

const GACardInput = (props: GACardInputProps) => {

    const defaultInputState = 
    {
        isTouched: false,
        isValid: false,
        isPotentiallyValid: true
    }

    const [cardNumber, setCardNumber] = React.useState();
    const [expiryDate, setExpiryDate] = React.useState();
    const [cvc, setCvc] = React.useState();
    const [zip, setZip] = React.useState();
    const [name, setName] = React.useState();
    const [cardValid, setCardValid] = React.useState<CardInputFieldState>(defaultInputState);
    const [expirationValid, setExpirationValid] = React.useState<CardInputFieldState>(defaultInputState);
    const [formattedExpiration, setFormattedExpiration] = React.useState<string>('');
    const [card, setCard] = React.useState<any>(null);
    const [cvcValid, setCvcValid] = React.useState<CardInputFieldState>(defaultInputState);
    const [zipValid, setZipValid] = React.useState<CardInputFieldState>(defaultInputState);
    const [nameValid, setNameValid] = React.useState<CardInputFieldState>(defaultInputState);

    const handleChangeCardNumber = (event: any) =>
    {
        setCardNumber(event.target.value);
        const cardValidation = valid.number(event.target.value);
        setCardValid(
            {
                isTouched: true,
                isValid: cardValidation.isValid,
                isPotentiallyValid: cardValidation.isPotentiallyValid,
            }
        );
        setCard(cardValidation.card);
    }

    const handleChangeExpiryDate = (event: any) =>
    {

        setExpiryDate(event.target.value);
        const expirationValidation = valid.expirationDate(event.target.value);
        setExpirationValid(
            {
                isTouched: true,
                isValid: expirationValidation.isValid,
                isPotentiallyValid: expirationValidation.isPotentiallyValid,
            }
        );
        //TODO: Make this so that it doesn't break in 2100.
        if(expirationValidation.year !== null && expirationValidation.month !== null)
        {
            console.log(expirationValidation);
            const expirationyear = (parseInt(expirationValidation.year,10) > 2000) ? expirationValidation.year : '20' + expirationValidation.year;
            console.log(expirationyear);
            setFormattedExpiration(expirationValidation.month + '/' + expirationyear);
        }
        else
        {
            setFormattedExpiration('');
        }
        
    }

    const handleChangeCVC = (event: any) =>
    {
        setCvc(event.target.value);
        const maxLength = card?.code?.size ?? 3;
        const expirationValidation = valid.cvv(event.target.value, maxLength);
        setCvcValid(
            {
                isTouched: true,
                isValid: expirationValidation.isValid,
                isPotentiallyValid: expirationValidation.isPotentiallyValid,
            }
        );
    }

    const handleChangeZip = (event: any) =>
    {
        setZip(event.target.value);
        const expirationValidation = valid.postalCode(event.target.value, {minLength: 5});
        setZipValid(
            {
                isTouched: true,
                isValid: expirationValidation.isValid,
                isPotentiallyValid: expirationValidation.isPotentiallyValid,
            }
        );
    }

    const handleChangeName = (event: any) =>
    {
        setName(event.target.value);
        const expirationValidation = valid.cardholderName(event.target.value, {minLength: 5});
        setNameValid(
            {
                isTouched: true,
                isValid: expirationValidation.isValid,
                isPotentiallyValid: expirationValidation.isPotentiallyValid,
            }
        );
    }
    
    React.useEffect(() => 
    {
        const totalValid = {
            zipValid: zipValid,
            expirationValid: expirationValid,
            cvcValid: cvcValid,
            nameValid: nameValid,
            cardValid: cardValid
        }

        //props.onCardValid(zipValid.isValid && nameValid.isValid && cardValid.isValid);
        props.onCardChange({
            cardNumber: cardNumber ?? '',
            expiryDate: formattedExpiration ?? '',
            cvc: cvc ?? '',
            zip: zip ?? '',
            name: name ?? ''
        });

        const errMessage = buildErrorMessage(totalValid);

        const finalValid = {...totalValid, errorMsg: errMessage} as CardInputState;
        if(props.onErrorMessageChange !== undefined)
        {
            props.onErrorMessageChange(finalValid);
        }

        props.onCardValid(totalValid.zipValid.isValid && totalValid.expirationValid.isValid && totalValid.cvcValid.isValid && totalValid.nameValid.isValid && totalValid.cardValid.isValid);

    },[cardNumber, expiryDate, cvc, zip, name, cvcValid, zipValid, expirationValid, nameValid, cardValid]);

    const buildErrorMessage = (totalValid: any): string [] =>
    {
        type ErrorMessages = Record<string, string>;
        const ErrorEnum: ErrorMessages = 
        {
            'zipValid': 'Invalid Zip Code',
            'expirationValid': 'Invalid Expiration Date',
            'cvcValid': 'Invalid Security Code',
            'nameValid': 'Invalid Cardholder Name',
            'cardValid': 'Invalid Card Number'
        };
        
        const errorArray = [] as string[];

        for(const [key, v] of Object.entries(totalValid))
        {
            const value = v as any;
            if(value.isValid === false && value.isTouched === true && value.isPotentiallyValid === false)
            {
                errorArray.push(ErrorEnum[key]);
            }
        }

        return errorArray;
    }

    return (
    <div>
        <div className='inputContainer'>
            <label className='inputLabel'>Card Number</label>
            <input className='cardInputCardNumber' disabled={props.disabled ?? false} onChange={handleChangeCardNumber} value={cardNumber} />
        </div>
        <div className='cardInputExpCvcZipLine'>
            <div className='inputContainer'>
                <label className='inputLabel'>Expiration Date</label>
                <input className='cardInputExpiration' disabled={props.disabled ?? false} onChange={handleChangeExpiryDate} value={expiryDate} />
            </div>
            <div className='inputContainer'>
                <label className='inputLabel'>Security Code</label>
                <input className='cardInputCvc' disabled={props.disabled ?? false} onChange={handleChangeCVC} value={cvc} />
            </div>
        </div>
        <div className='inputContainer'>
                <label className='inputLabel'>Zip Code</label>
                <input className='cardInputZipCode' disabled={props.disabled ?? false} placeholder="Zip Code" maxLength={6} type='tel' value={zip} onChange={handleChangeZip} />
        </div>
        <div className='inputContainer'>
            <label className='inputLabel'>Cardholder Name</label>
            <input className='cardInputName' disabled={props.disabled ?? false} placeholder="Cardholder Name" value={name} onChange={handleChangeName} />
        </div>
    </div>
    );
}

export default GACardInput;